import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useNavigate } from "react-router-dom";
import {
  HiX,
  HiSearch,
  HiLocationMarker,
  HiBriefcase,
  HiArrowRight,
} from "react-icons/hi";
import { useApplicationForm } from "../hooks/useApplicationForm";
import ApplicationFormModal from "../components/ApplicationFormModal";
import { openPositions } from "../data/jobsData";

const CareersPage = () => {
  const { showForm, setShowForm, formData, handleChange, resetForm } = useApplicationForm();
const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState("");
  const [expandedSkills, setExpandedSkills] = useState({});
  const [expandedCards, setExpandedCards] = useState({});

  // Scroll to top on component mount
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  // Auto-scroll to open positions when searching
  useEffect(() => {
    if (searchTerm.trim()) {
      const openPositionsSection = document.getElementById("open-positions-section");
      if (openPositionsSection) {
        setTimeout(() => {
          openPositionsSection.scrollIntoView({ behavior: "smooth", block: "start" });
        }, 100);
      }
    }
  }, [searchTerm]);

  const handleDepartmentClick = (path) => {
    navigate(path);
  };


  const benefits = [
    {
      icon: "💰",
      title: "Competitive Salary",
      description: "Attractive compensation packages.",
    },
    {
      icon: "🏠",
      title: "Work Anywhere",
      description: "Remote & hybrid flexibility.",
    },
    {
      icon: "📚",
      title: "Learning & Development",
      description: "Training & certifications.",
    },
    {
      icon: "⚖️",
      title: "Work-Life Balance",
      description: "Flexible schedules & leave policies.",
    },
    {
      icon: "🤝",
      title: "Collaborative Culture",
      description: "Supportive professional teams.",
    },
    {
      icon: "🎯",
      title: "Career Growth",
      description: "Clear advancement opportunities.",
    },
  ];

  const departments = [
    {
      name: "Engineering",
      icon: "💻",
      path: "/careers/engineering",
      count: "Full-Stack Developers",
      description: "Build with cutting-edge tech.",
    },
    {
      name: "Customer Success",
      icon: "✨",
      path: "/careers/customer-success",
      count: "Support Specialists",
      description: "Help clients succeed.",
    },
    {
      name: "Business Development",
      icon: "📈",
      path: "/careers/business-development",
      count: "Growth Experts",
      description: "Expand reach & revenue.",
    },
    {
      name: "Design & UX",
      icon: "🎨",
      path: "/careers/design-ux",
      count: "Creative Designers",
      description: "Craft intuitive experiences.",
    },
  ];

  // FORM HANDLERS


  // UI STARTS HERE
  // Compute filtered positions based on hero search term
  const filteredPositions = openPositions.filter((position) =>
    [
      position.title,
      position.description,
      position.location,
      position.department,
      ...(position.requirements || []),
    ]
      .join(" ")
      .toLowerCase()
      .includes(searchTerm.toLowerCase())
  );


  return (
    <div className="pb-20 min-h-screen bg-white">
      {/* HERO SECTION - RE-DESIGNED */}
      <section className="relative min-h-[80vh] flex items-center pt-28 pb-16 overflow-hidden bg-[#f8fafc]">
        {/* Animated Background Elements */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          {/* Main Mesh Gradients */}
          <motion.div
            animate={{
              scale: [1, 1.2, 1],
              rotate: [0, 45, 0],
              opacity: [0.3, 0.4, 0.3],
            }}
            transition={{ duration: 15, repeat: Infinity, ease: "easeInOut" }}
            className="absolute -top-[20%] -left-[10%] w-[70%] h-[70%] bg-primary-200/40 rounded-full blur-[120px]"
          />
          <motion.div
            animate={{
              scale: [1.2, 1, 1.2],
              rotate: [0, -45, 0],
              opacity: [0.2, 0.3, 0.2],
            }}
            transition={{ duration: 18, repeat: Infinity, ease: "easeInOut", delay: 2 }}
            className="absolute -bottom-[20%] -right-[10%] w-[60%] h-[60%] bg-secondary-200/30 rounded-full blur-[100px]"
          />
          <motion.div
            animate={{
              y: [0, 50, 0],
              opacity: [0.1, 0.15, 0.1],
            }}
            transition={{ duration: 12, repeat: Infinity, ease: "easeInOut" }}
            className="absolute top-1/4 left-1/3 w-[40%] h-[40%] bg-purple-200/20 rounded-full blur-[80px]"
          />

          {/* Grid Pattern */}
          <div className="absolute inset-0 bg-[linear-gradient(to_right,#e2e8f00a_1px,transparent_1px),linear-gradient(to_bottom,#e2e8f00a_1px,transparent_1px)] bg-[size:40px_40px] [mask-image:radial-gradient(ellipse_60%_50%_at_50%_50%,#000_70%,transparent_100%)]"></div>

          {/* Floating Job Tags Decoration */}
          <div className="hidden lg:block absolute inset-0">
            {[
              { text: "Engineering", color: "from-blue-400 to-blue-600", top: "20%", left: "15%", delay: 0 },
              { text: "Full Stack", color: "from-indigo-400 to-indigo-600", top: "42%", left: "8%", delay: 0.7 },
              { text: "Customer Service", color: "from-pink-400 to-purple-600", top: "65%", left: "12%", delay: 1 },
              { text: "Business", color: "from-emerald-400 to-teal-600", top: "20%", right: "15%", delay: 0.5 },
              { text: "Cloud Architecture", color: "from-cyan-400 to-cyan-600", top: "42%", right: "8%", delay: 1.2 },
              { text: "UI/UX", color: "from-amber-400 to-orange-600", top: "65%", right: "12%", delay: 1.5 },
            ].map((tag, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 20 }}
                animate={{
                  opacity: 0.6,
                  y: [0, -20, 0],
                  x: [0, 10, 0]
                }}
                transition={{
                  opacity: { duration: 1 },
                  y: { duration: 4 + i, repeat: Infinity, ease: "easeInOut", delay: tag.delay },
                  x: { duration: 5 + i, repeat: Infinity, ease: "easeInOut", delay: tag.delay }
                }}
                className="absolute"
                style={{ top: tag.top, left: tag.left, right: tag.right }}
              >
                <div className={`px-4 py-2 rounded-full bg-white/60 backdrop-blur-md border border-slate-200 text-slate-800 text-xs font-bold uppercase tracking-widest shadow-sm`}>
                  <span className={`bg-gradient-to-r ${tag.color} bg-clip-text text-transparent`}>
                    {tag.text}
                  </span>
                </div>
              </motion.div>
            ))}
          </div>
        </div>

        <div className="container-custom relative z-10 px-4">
          <div className="max-w-4xl mx-auto text-center">
            {/* Status Badge */}
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.5 }}
              className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-primary-50 border border-primary-100 text-primary-600 text-sm font-semibold mb-6 backdrop-blur-sm"
            >
              <span className="w-2 h-2 rounded-full bg-primary-500 animate-pulse"></span>
              Join our growing team
            </motion.div>

            {/* Main Heading */}
            <motion.h1
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, ease: "easeOut" }}
              className="text-5xl md:text-6xl lg:text-6xl font-black text-slate-900 mb-6 tracking-tight leading-[1.05]"
            >
              Building the Future of <br />
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-primary-600 via-secondary-500 to-primary-600 bg-gradient-300 animate-gradient">
                Technology
              </span>
            </motion.h1>

            {/* Description */}
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}
              className="text-lg md:text-xl text-slate-600 max-w-2xl mx-auto mb-10 leading-relaxed font-medium"
            >
              Our cloud-based software enables businesses to innovate and scale. We're looking for the best minds to help us build it.
            </motion.p>

            {/* Search Container */}
            <motion.div
              initial={{ opacity: 0, y: 40 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.4 }}
              className="relative max-w-md mx-auto group"
            >
              <div className="absolute -inset-1 bg-gradient-to-r from-primary-500 to-secondary-500 rounded-2xl blur opacity-25 group-hover:opacity-40 transition duration-1000 group-hover:duration-200"></div>

              <div className="relative flex flex-col sm:flex-row items-center gap-1 bg-white/70 backdrop-blur-xl p-1.5 rounded-2xl border border-slate-200 shadow-xl">
                <div className="relative flex-1 w-full flex items-center group/input">
                  <HiSearch className="absolute left-4 w-6 h-6 text-slate-500 group-focus-within/input:text-primary-600 transition-colors" />
                  <input
                    type="text"
                    placeholder="Search by job title, skill, or location..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="w-full bg-transparent pl-12 pr-4 py-2.5 text-slate-900 placeholder-slate-400 focus:outline-none text-base font-medium"
                  />
                  {searchTerm && (
                    <button
                      onClick={() => setSearchTerm("")}
                      className="absolute right-4 p-1 rounded-full hover:bg-slate-100 text-slate-500 transition-all"
                    >
                      <HiX className="w-5 h-5" />
                    </button>
                  )}
                </div>

                <div className="flex w-full sm:w-auto gap-2 p-1">
                  <motion.button
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    onClick={() => setShowForm(true)}
                    className="flex-1 sm:px-6 py-2.5 bg-primary-600 hover:bg-primary-500 text-white rounded-xl font-bold shadow-lg shadow-primary-600/30 transition-all flex items-center justify-center whitespace-nowrap"
                  >
                    <HiBriefcase className="w-5 h-5 mr-2" /> Apply Now
                  </motion.button>
                </div>
              </div>

              {/* Search Results Preview */}
              <AnimatePresence>
                {searchTerm && (
                  <motion.div
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: 10 }}
                    className="absolute top-full left-0 right-0 mt-4 text-center"
                  >
                    <p className="text-slate-500 font-medium">
                      Showing <span className="text-slate-900 font-bold">{filteredPositions.length}</span> positions available
                    </p>
                  </motion.div>
                )}
              </AnimatePresence>
            </motion.div>
          </div>
        </div>

      </section>

      {/* BENEFITS */}
      <section className="py-20 bg-neutral-50">
        <div className="container-custom px-4 lg:px-8">
          <h2 className="text-4xl font-bold text-center mb-8">
            Work where it's best for you
          </h2>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8">
            {benefits.map((benefit, index) => (
              <motion.div
                key={index}
                className="bg-white p-8 rounded-2xl shadow-lg"
              >
                <div className="text-5xl mb-4">{benefit.icon}</div>
                <h3 className="text-xl font-bold">{benefit.title}</h3>
                <p className="text-gray-600 mt-2">{benefit.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* DEPARTMENTS */}
      <section className="py-10 bg-white">
        <div className="container-custom px-4 lg:px-8">
          <h2 className="text-4xl font-bold text-center mb-8">
            Find your new team
          </h2>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {departments.map((dept, index) => (
              <motion.div
                key={index}
                onClick={() => handleDepartmentClick(dept.path)}
                className="bg-gradient-to-br from-primary-50 to-white p-8 rounded-2xl border-2 cursor-pointer"
              >
                <div className="flex justify-between mb-4">
                  <span className="text-4xl">{dept.icon}</span>
                  <HiArrowRight className="w-10 h-10 bg-primary-500 text-white p-2 rounded-lg" />
                </div>
                <h3 className="text-2xl font-bold">{dept.name}</h3>
                <p className="text-primary-600 font-semibold">{dept.count}</p>
                <p className="text-gray-600 mt-2">{dept.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* OPEN POSITIONS */}
      <section id="open-positions-section" className="py-16 md:py-20 bg-gradient-to-b from-neutral-50 via-white to-neutral-50">
        <div className="container-custom px-4 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
              Open Positions
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Explore career opportunities to join our innovative team. We're hiring talented professionals who are passionate about technology and growth.
            </p>
          </motion.div>

          {/* Job Cards Grid */}
          <div className="space-y-3 max-w-5xl mx-auto">
            {filteredPositions.map((position, index) => (
              <motion.div
                key={position.id}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.08 }}
                className="relative"
              >
                {/* Collapsed View - Thin Rectangle */}
                <motion.div
                  className="bg-white border border-gray-200 rounded-lg shadow-sm hover:shadow-md transition-all duration-300 overflow-hidden"
                >
                  <button
                    onClick={() => setExpandedCards(prev => ({ ...prev, [position.id]: !prev[position.id] }))}
                    className="w-full text-left"
                  >
                    <div className="flex items-center justify-between p-4 md:p-5 gap-4">
                      {/* Left Content */}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-3 mb-2">
                          <div className="inline-flex items-center gap-2 px-2.5 py-1 bg-primary-50 rounded-full flex-shrink-0">
                            <span className="w-1.5 h-1.5 bg-primary-600 rounded-full"></span>
                            <span className="text-xs font-semibold text-primary-700 uppercase tracking-wide">
                              {position.department}
                            </span>
                          </div>
                          <span className="px-2 py-0.5 bg-blue-50 text-blue-700 rounded text-xs font-semibold uppercase tracking-wide flex-shrink-0">
                            {position.type}
                          </span>
                        </div>
                        <h3 className="text-sm md:text-base font-bold text-gray-900 truncate">
                          {position.title}
                        </h3>
                        <div className="flex items-center gap-1.5 text-gray-600 text-xs mt-1">
                          <HiLocationMarker className="w-4 h-4 text-primary-600 flex-shrink-0" />
                          <span className="truncate">{position.location}</span>
                        </div>
                      </div>

                      {/* Expand/Collapse Icon */}
                      <motion.div
                        animate={{ rotate: expandedCards[position.id] ? 180 : 0 }}
                        className="p-2 hover:bg-gray-100 rounded-lg flex-shrink-0 transition-all text-gray-600"
                      >
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 14l-7 7m0 0l-7-7m7 7V3" />
                        </svg>
                      </motion.div>
                    </div>
                  </button>

                  {/* Expanded View */}
                  <AnimatePresence>
                    {expandedCards[position.id] && (
                      <motion.div
                        initial={{ opacity: 0, height: 0 }}
                        animate={{ opacity: 1, height: "auto" }}
                        exit={{ opacity: 0, height: 0 }}
                        transition={{ duration: 0.3 }}
                        className="border-t border-gray-100 bg-gray-50"
                      >
                        <div className="p-4 md:p-5 space-y-4">
                          {/* Description */}
                          <div>
                            <p className="text-xs font-semibold text-gray-700 uppercase tracking-widest mb-2">
                              About the Role
                            </p>
                            <p className="text-gray-700 text-sm leading-relaxed">
                              {position.description}
                            </p>
                          </div>

                          {/* Key Requirements */}
                          <div>
                            <p className="text-xs font-semibold text-gray-700 uppercase tracking-widest mb-2">
                              Key Requirements
                            </p>
                            <div className="flex flex-wrap gap-2">
                              {expandedSkills[position.id]
                                ? position.requirements.map((req, idx) => (
                                  <span
                                    key={idx}
                                    className="px-2 py-1 bg-white border border-gray-300 rounded text-gray-700 text-xs font-medium"
                                  >
                                    {req}
                                  </span>
                                ))
                                : position.requirements.slice(0, 3).map((req, idx) => (
                                  <span
                                    key={idx}
                                    className="px-2 py-1 bg-white border border-gray-300 rounded text-gray-700 text-xs font-medium"
                                  >
                                    {req}
                                  </span>
                                ))
                              }
                              {position.requirements.length > 3 && (
                                <motion.button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setExpandedSkills(prev => ({ ...prev, [position.id]: !prev[position.id] }));
                                  }}
                                  whileHover={{ scale: 1.05 }}
                                  whileTap={{ scale: 0.95 }}
                                  className="px-2 py-1 bg-primary-50 text-primary-700 rounded text-xs font-semibold border border-primary-200 hover:bg-primary-100 transition-all duration-200 cursor-pointer"
                                >
                                  {expandedSkills[position.id] ? "- Show Less" : `+ ${position.requirements.length - 3} More`}
                                </motion.button>
                              )}
                            </div>
                          </div>

                          {/* Apply Button */}
                          <motion.button
                            onClick={() => setShowForm(true)}
                            whileHover={{ scale: 1.02 }}
                            whileTap={{ scale: 0.98 }}
                            className="w-full bg-gradient-to-r from-primary-600 to-primary-700 hover:from-primary-700 hover:to-primary-800 text-white py-2.5 px-4 rounded-lg font-semibold text-sm flex items-center justify-center gap-2 transition-all duration-300 shadow-md hover:shadow-lg"
                          >
                            <span>Apply Now</span>
                            <HiArrowRight className="w-4 h-4" />
                          </motion.button>
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </motion.div>
              </motion.div>
            ))}
          </div>

          {/* No Results State */}
          {filteredPositions.length === 0 && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="text-center py-16"
            >
              <div className="text-5xl mb-4">🔍</div>
              <p className="text-gray-600 text-lg font-medium mb-2">No matching positions found</p>
              <p className="text-gray-500 text-sm">Try adjusting your search or check back soon for new opportunities.</p>
            </motion.div>
          )}

          {/* CTA Section */}
          {filteredPositions.length > 0 && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="mt-12 text-center"
            >
              <p className="text-gray-600 text-base md:text-lg mb-4">
                Don't see a role that fits? We're always looking for talented professionals!
              </p>
              <motion.button
                onClick={() => setShowForm(true)}
                whileHover={{ scale: 1.05 }}
                className="inline-flex items-center gap-2 px-8 py-3 bg-white border-2 border-primary-600 text-primary-600 rounded-xl font-semibold hover:bg-primary-600 hover:text-white transition-all duration-300"
              >
                Send Your Resume
                <HiArrowRight className="w-5 h-5" />
              </motion.button>
            </motion.div>
          )}
        </div>
      </section>



      {/* APPLICATION FORM MODAL */}
      <ApplicationFormModal showForm={showForm} setShowForm={setShowForm} formData={formData} handleChange={handleChange} resetForm={resetForm} />



    </div>
  );
};

export default CareersPage;
